/* eslint-disable @typescript-eslint/no-unused-vars */
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    FormControlLabel,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Select,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Formik } from '../../lib/formik/Formik';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MFTextField } from '../../lib/formik';
import MFSelectField from '../../lib/formik/SelectField';
import { SearchableSelect } from '../../lib/formik/searchSelectField';
import { TextDatePicker } from '../onboarding/DatePickerWithTextFeild';
import { DataTable } from '../DataTable';
import { ProceedSaveLater, SubHeading } from '../investors/components';
import { ConfirmationDialog, AmlPopup, AmlPopupValues } from '../commonComponents';
import { updateApplication, FetchData } from '../../redux-store/actions/application';
import {
    applicationComparison,
    isModuleAllowed,
    removeSingleQuote,
    saveForLater,
} from '../../utils/utilityFunctions';
import { showError } from '../../redux-store/actions/auth';
import {
    ONBOARDING_DEMAT_DETAILS,
    USER_ROLES,
    MAX_REQUIRED_AUTHORISED_SIGNATORIES,
    CpUboCodesMaster,
    genderMasters,
    uboOccupationDetailsMasters,
} from '../../utils/constant';
import { getNationalityList } from '../../redux-store/actions';
import { mdmsCountriesList, nationaliyType } from '../../redux-store/types/mdms';
import { RootStateType } from '../../redux-store/reducers';
import { RelatedParty, RelatedPartyType, AmlSystemMatch } from '../../redux-store/types/api-types';
import { useHistory } from 'react-router';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
    individualPanRegex,
    emailRegex,
    numberRegex,
    indianPin,
    alphaNumeric,
    addressRegex,
    aadhaarRegex,
} from '../../utils/regex';
import dayjs from '../../utils/dayjs';

const MAX_RELATED_PARTIES = 5;

const identityTypeOptions = [
    { key: 'PAN', value: 'PAN' },
    { key: 'AADHAR', value: 'AADHAR' },
    { key: 'Voter', value: 'Voter' },
    { key: 'Passport', value: 'Passport' },
];

const addressTypeOptions = [
    { key: 'Resident/Business', value: 'Resident/Business' },
    { key: 'Resident', value: 'Resident' },
    { key: 'Business', value: 'Business' },
    { key: 'Registered Office', value: 'Registered Office' },
    { key: 'Unspecified', value: 'Unspecified' },
];

const hasType = (party: RelatedParty, type: string): boolean => {
    return party.relatedPartyTypes?.some((t) => t.type === type && t.isActive) ?? false;
};

const typeLabelsMap: Record<string, string> = {
    director: 'Director',
    promoter: 'Promoter',
    karta: 'Karta',
    trustee: 'Trustee',
    partner: 'Partner',
    courtAppointed: 'Court Appointed Official Proprietor',
    beneficiary: 'Beneficiary',
    authorisedsignatory: 'Authorized Signatory',
    ubo: 'Beneficial Owner (UBO)',
    powerOfAttorneyHolder: 'PoA Holder',
    others: 'Others',
};

const getActiveTypes = (party: RelatedParty): string[] => {
    return (party.relatedPartyTypes || [])
        .filter((t) => t.isActive)
        .map((t) => {
            if (t.type === 'others' && party.otherRelatedPartyType) {
                return `Others (${party.otherRelatedPartyType})`;
            }
            return typeLabelsMap[t.type] || t.type;
        });
};

const createInitialParty = (): RelatedParty => ({
    name: '',
    pan: '',
    mobile: '',
    countryCode: '+91',
    countryNameAndCode: 'India: +91',
    email: '',
    designation: '',
    canEsign: false,
    identificationType: '',
    dob: '',
    countryOfTaxResidency: '',
    percentageOfBeneficialInterest: 0,
    cpUboCode: '',
    placeAndCountryOfBirth: '',
    ckycNumber: '',
    permanentAddress1: '',
    permanentAddress2: '',
    permanentAddress3: '',
    permanentCity: '',
    permanentState: '',
    permanentCountry: '',
    permanentPincode: '',
    gender: '',
    fatherOrSpouseName: '',
    occupation: '',
    nationality: '',
    din: '',
    identificationNumber: '',
    addressType: '',
    correspondenceAddressType: '',
    permanentAddressType: '',
    permanentAddressSameAsCorrespondence: 'N',
    correspondenceAddress1: '',
    correspondenceAddress2: '',
    correspondenceAddress3: '',
    correspondenceCity: '',
    correspondenceState: '',
    correspondenceCountry: '',
    correspondencePincode: '',
    relatedPartyTypes: [
        { type: 'director', isActive: true },
        { type: 'promoter', isActive: false },
        { type: 'karta', isActive: false },
        { type: 'trustee', isActive: false },
        { type: 'partner', isActive: false },
        { type: 'courtAppointed', isActive: false },
        { type: 'beneficiary', isActive: false },
        { type: 'authorisedsignatory', isActive: false },
        { type: 'ubo', isActive: false },
        { type: 'powerOfAttorneyHolder', isActive: false },
        { type: 'others', isActive: false },
    ],
    otherRelatedPartyType: '',
    amlCheck: false,
    amlComments: '',
    amlAcknowledgement: false,
    amlCommentedRole: '',
    amlCommentedRoleId: '',
});

const headers = [
    { header: 'NAME', valueGetter: (row: RelatedParty) => row.name || 'N/A' },
    { header: 'PAN', valueGetter: (row: RelatedParty) => row.pan?.toUpperCase() || 'N/A' },
    {
        header: 'TYPE OF RELATED PARTY', renderCell: (row: RelatedParty) => {
            const types = getActiveTypes(row);
            if (types.length === 0) return <Typography>N/A</Typography>;
            const display = types.slice(0, 2).join(', ');
            const remaining = types.length > 2 ? types.slice(2) : [];
            return remaining.length > 0 ? (
                <Tooltip title={types.join(', ')} arrow>
                    <Typography sx={{ cursor: 'pointer' }}>{display}, +{remaining.length} more</Typography>
                </Tooltip>
            ) : (
                <Typography>{display}</Typography>
            );
        }
    },
    { header: 'DIN', valueGetter: (row: RelatedParty) => row.din || 'N/A' },
];

const validateParty = (values: RelatedParty, allParties?: RelatedParty[]): Record<string, string> => {
    const errors: Record<string, string> = {};
    const isAS = hasType(values, 'authorisedsignatory');
    const isUBO = hasType(values, 'ubo');
    const isDir = hasType(values, 'director');

    // Name
    if (!values.name) errors.name = 'Name is required';

    // PAN - required, 10 chars, individual PAN regex, uniqueness
    if (!values.pan) { errors.pan = 'PAN is required'; }
    else if (values.pan.length !== 10) { errors.pan = 'PAN must be exactly 10 characters'; }
    else if (!individualPanRegex.test(values.pan.toUpperCase())) { errors.pan = 'Only individual PANs are allowed'; }
    else if (allParties) {
        const otherPans = allParties.filter((p) => p !== values).map((p) => p.pan?.toUpperCase());
        if (otherPans.includes(values.pan.toUpperCase())) { errors.pan = 'PAN already exists for another related party'; }
    }

    // DOB - required, not future, age checks
    if (!values.dob) { errors.dob = 'Date of Birth is required'; }
    else if (`${values.dob}`.toLowerCase() === 'invalid date') { errors.dob = 'Date of Birth should be a valid date'; }
    else if (dayjs(values.dob).isAfter(dayjs(), 'day')) { errors.dob = 'Date of Birth cannot be a future date'; }
    else if (dayjs().diff(dayjs(values.dob), 'year') < 18) { errors.dob = 'Age should be greater than 18'; }

    // Father/Spouse
    if (!values.fatherOrSpouseName) errors.fatherOrSpouseName = 'Father/Spouse Name is required';

    // Identity Type, ID Number, DIN - only for Directors
    if (isDir) {
        if (!values.identificationType) errors.identificationType = 'Identity Type is required';
        if (!values.din) errors.din = 'DIN is required';
        if (!values.identificationNumber) {
            errors.identificationNumber = 'ID Number is required';
        } else if (values.identificationType === 'PAN' && !individualPanRegex.test(values.identificationNumber.toUpperCase())) {
            errors.identificationNumber = 'Invalid PAN format';
        } else if (values.identificationType === 'AADHAR' && !/^\d{4}$/.test(values.identificationNumber)) {
            errors.identificationNumber = 'Enter last 4 digits of Aadhaar';
        }
    }

    // Gender - required only for UBO
    if (isUBO && !values.gender) errors.gender = 'Gender is required';

    // Occupation - required only for UBO, must be valid value
    if (isUBO) {
        if (!values.occupation) { errors.occupation = 'Occupation is required'; }
        else if (!uboOccupationDetailsMasters.includes(values.occupation)) { errors.occupation = 'Invalid value for Occupation'; }
    }

    // Nationality - required only for UBO
    if (isUBO && !values.nationality) errors.nationality = 'Nationality is required';

    // Email - required, regex, uniqueness
    if (!values.email) { errors.email = 'Email is required'; }
    else if (!emailRegex.test(values.email)) { errors.email = 'Invalid Email ID'; }
    else if (allParties) {
        const otherEmails = allParties.filter((p) => p !== values).map((p) => p.email);
        if (otherEmails.includes(values.email)) { errors.email = 'Email already exists for another related party'; }
    }

    // Mobile - required, numeric, 10 digits for India, uniqueness
    if (!values.mobile) { errors.mobile = 'Mobile is required'; }
    else if (!numberRegex.test(values.mobile)) { errors.mobile = 'Invalid Mobile number'; }
    else if (values.countryNameAndCode === 'India: +91' && values.mobile.length !== 10) { errors.mobile = 'Please enter a valid 10 digit mobile number'; }
    else if (allParties) {
        const otherMobiles = allParties.filter((p) => p !== values).map((p) => p.mobile);
        if (otherMobiles.includes(values.mobile)) { errors.mobile = 'Mobile number already exists for another related party'; }
    }

    // Country code
    if (!values.countryNameAndCode) errors.countryNameAndCode = 'Country Code is required';

    // AS-specific
    if (isAS && !values.designation) errors.designation = 'Designation is required';

    // Others - specify field required
    if (hasType(values, 'others') && !values.otherRelatedPartyType) errors.otherRelatedPartyType = 'Please specify the other type';

    // UBO-specific
    if (isUBO) {
        if (!values.countryOfTaxResidency) errors.countryOfTaxResidency = 'Country of Tax Residency is required';
        const pct = Number(values.percentageOfBeneficialInterest);
        if (pct === null || pct === undefined) { errors.percentageOfBeneficialInterest = 'UBO % is required'; }
        else if (pct <= 0) { errors.percentageOfBeneficialInterest = 'UBO % must be greater than 0'; }
        else if (pct > 100) { errors.percentageOfBeneficialInterest = 'UBO % must not exceed 100'; }
        if (!values.cpUboCode) { errors.cpUboCode = 'UBO Code is required'; }
        else if (!Object.keys(CpUboCodesMaster).includes(values.cpUboCode)) { errors.cpUboCode = 'Invalid UBO Code'; }
        if (!values.placeAndCountryOfBirth) errors.placeAndCountryOfBirth = 'Place of Birth is required';
        if (values.ckycNumber) {
            if (values.ckycNumber.length !== 14) errors.ckycNumber = 'Invalid CKYC Number';
            else if (!numberRegex.test(values.ckycNumber)) errors.ckycNumber = 'CKYC Number allows only digits';
        }
    }

    // Correspondence Address - required for all RP types
    if (!values.correspondenceAddressType) errors.correspondenceAddressType = 'Address Type is required';
    if (!values.correspondenceAddress1) { errors.correspondenceAddress1 = 'Address Line 1 is required'; }
    else if (!addressRegex.test(values.correspondenceAddress1)) { errors.correspondenceAddress1 = "Invalid address format [special characters not allowed except -/',&()#:.]"; }
    if (!values.correspondenceAddress2) { errors.correspondenceAddress2 = 'Address Line 2 is required'; }
    else if (!addressRegex.test(values.correspondenceAddress2)) { errors.correspondenceAddress2 = "Invalid address format"; }
    if (!values.correspondenceAddress3) { errors.correspondenceAddress3 = 'Address Line 3 is required'; }
    else if (values.correspondenceAddress3 && !addressRegex.test(values.correspondenceAddress3)) { errors.correspondenceAddress3 = "Invalid address format"; }
    if (!values.correspondenceCity) errors.correspondenceCity = 'City is required';
    if (!values.correspondenceState) errors.correspondenceState = 'State is required';
    if (!values.correspondenceCountry) errors.correspondenceCountry = 'Country is required';
    if (!values.correspondencePincode) { errors.correspondencePincode = 'Pincode is required'; }
    else if (values.correspondenceCountry?.toLowerCase() === 'india' && !indianPin.test(values.correspondencePincode)) { errors.correspondencePincode = 'Pincode should be 6 digits'; }
    else if (values.correspondenceCountry?.toLowerCase() !== 'india' && !alphaNumeric.test(values.correspondencePincode)) { errors.correspondencePincode = 'Invalid Pincode'; }

    // Permanent Address (if not same as correspondence)
    if (values.permanentAddressSameAsCorrespondence !== 'Y') {
        if (!values.permanentAddressType) errors.permanentAddressType = 'Address Type is required';
        if (!values.permanentAddress1) { errors.permanentAddress1 = 'Address Line 1 is required'; }
        else if (!addressRegex.test(values.permanentAddress1)) { errors.permanentAddress1 = "Invalid address format"; }
        if (!values.permanentAddress2) { errors.permanentAddress2 = 'Address Line 2 is required'; }
        else if (!addressRegex.test(values.permanentAddress2)) { errors.permanentAddress2 = "Invalid address format"; }
        if (!values.permanentAddress3) { errors.permanentAddress3 = 'Address Line 3 is required'; }
        else if (values.permanentAddress3 && !addressRegex.test(values.permanentAddress3)) { errors.permanentAddress3 = "Invalid address format"; }
        if (!values.permanentCity) errors.permanentCity = 'City is required';
        if (!values.permanentState) errors.permanentState = 'State is required';
        if (!values.permanentCountry) errors.permanentCountry = 'Country is required';
        if (!values.permanentPincode) { errors.permanentPincode = 'Pincode is required'; }
        else if (values.permanentCountry?.toLowerCase() === 'india' && !indianPin.test(values.permanentPincode)) { errors.permanentPincode = 'Pincode should be 6 digits'; }
        else if (values.permanentCountry?.toLowerCase() !== 'india' && !alphaNumeric.test(values.permanentPincode)) { errors.permanentPincode = 'Invalid Pincode'; }
    }

    return errors;
};

function FormPageView({ initialParties, isEdit, onSave, onCancel, countriesList, currentActiveCount, existingParties }: {
    initialParties: RelatedParty[];
    isEdit: boolean;
    onSave: (parties: RelatedParty[]) => void;
    onCancel: () => void;
    countriesList: mdmsCountriesList[];
    currentActiveCount: number;
    existingParties: RelatedParty[];
}): JSX.Element {
    const [parties, setParties] = useState<RelatedParty[]>(initialParties);
    const formRefs = React.useRef<Record<number, RelatedParty>>({});
    const dispatch = useDispatch();

    const canAddMore = !isEdit && (currentActiveCount + parties.length) < MAX_RELATED_PARTIES;

    const handleAddAnother = () => {
        if (canAddMore) {
            setParties([...parties, createInitialParty()]);
        }
    };

    const handleRemoveParty = (index: number) => {
        setParties(parties.filter((_, i) => i !== index));
        // Clean up ref
        delete formRefs.current[index];
    };

    const getLatestParties = (): RelatedParty[] => {
        return parties.map((p, i) => formRefs.current[i] || p);
    };

    const handleSaveAll = () => {
        const latestParties = getLatestParties();
        // Check each party has at least one type checked
        for (let i = 0; i < latestParties.length; i++) {
            const hasAnyType = latestParties[i].relatedPartyTypes?.some((t) => t.isActive);
            if (!hasAnyType) {
                dispatch(showError(`Please select at least one checkbox under 'Type of Related Party'`));
                return;
            }
        }
        // Validate all parties with cross-party uniqueness checks
        for (let i = 0; i < latestParties.length; i++) {
            const allForValidation = [...existingParties, ...latestParties.filter((_, idx) => idx !== i)];
            const errs = validateParty(latestParties[i], allForValidation);
            if (Object.keys(errs).length > 0) {
                const formEl = document.getElementById(`related-party-form-${i}`);
                if (formEl) {
                    const submitBtn = formEl.querySelector('button[type="submit"]') as HTMLButtonElement;
                    submitBtn?.click();
                }
                return;
            }
        }
        // Sync permanent address from correspondence if same-as is checked
        const finalParties = latestParties.map((p) => {
            if (p.permanentAddressSameAsCorrespondence === 'Y') {
                return {
                    ...p,
                    permanentAddressType: p.correspondenceAddressType,
                    permanentAddress1: p.correspondenceAddress1,
                    permanentAddress2: p.correspondenceAddress2,
                    permanentAddress3: p.correspondenceAddress3,
                    permanentCity: p.correspondenceCity,
                    permanentState: p.correspondenceState,
                    permanentCountry: p.correspondenceCountry,
                    permanentPincode: p.correspondencePincode,
                };
            }
            return p;
        });
        onSave(finalParties);
    };

    return (
        <Grid container rowSpacing={1} sx={{ width: '100%', ml: 0, '.MuiGrid-item': { px: { xs: 0, sm: '15px' } } }}>
            {/* Back + Title */}
            <Grid item xs={12} display="flex" alignItems="center" mb={3}>
                <IconButton onClick={onCancel} aria-label="Go back">
                    <ArrowBackIcon sx={{ color: 'primary.main' }} />
                </IconButton>
                <Typography variant="h2" color="text.primary" sx={{ ml: 1, fontWeight: 600 }}>
                    {isEdit ? 'Edit New Party' : 'Add New Party'}
                </Typography>
            </Grid>

            {/* Add Another Party button */}
            {canAddMore && (
                <Grid item xs={12} display="flex" justifyContent="flex-end" sx={{ mb: 2 }}>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddAnother}
                        sx={{ color: 'primary.main', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>
                        Add Another Related Party
                    </Button>
                </Grid>
            )}

            {parties.map((party, partyIndex) => (
                <Grid item xs={12} key={partyIndex} sx={{ mb: 3 }}>
                    {parties.length > 1 && (
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="body_500" color="primary.main" sx={{ fontWeight: 600 }}>
                                Related Party {currentActiveCount + partyIndex + 1}
                            </Typography>
                            <IconButton
                                onClick={() => handleRemoveParty(partyIndex)}
                                aria-label={`Remove Related Party ${currentActiveCount + partyIndex + 1}`}
                                size="small"
                                sx={{ ':hover': { bgcolor: 'error.50' } }}
                            >
                                <DeleteOutlineIcon color="error" fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                    <Formik
                        initialValues={party}
                        validate={(values) => validateParty(values, [...existingParties, ...getLatestParties().filter((_, idx) => idx !== partyIndex)])}
                        onSubmit={() => { return; }}
                    >
                        {({ values, setFieldValue, setValues, handleSubmit: formikSubmit, submitForm }) => {
                            const isAS = hasType(values, 'authorisedsignatory');
                            const isUBO = hasType(values, 'ubo');
                            const isExistingParty = (!!party.groupId || !!party.applicationId);
                            const [fetchLoading, setFetchLoading] = useState(false);
                            const [panVerified, setPanVerified] = useState(isExistingParty || !!party.amlCheck || !!party.name);
                            const [amlCheckPassed, setAmlCheckPassed] = useState(party.amlCheck || false);
                            const [amlPopupOpen, setAmlPopupOpen] = useState(false);
                            const [amlFailedResponse, setAmlFailedResponse] = useState<AmlSystemMatch | undefined>(undefined);
                            // On edit: all fields visible, PAN/DOB disabled, no fetch needed
                            const showAllFields = isExistingParty || (panVerified && (amlCheckPassed || values.amlAcknowledgement));

                            const toggleType = (type: string, checked: boolean) => {
                                const existingType = values.relatedPartyTypes.find((t) => t.type === type);
                                let updated;
                                if (existingType) {
                                    updated = values.relatedPartyTypes.map((t) =>
                                        t.type === type ? { ...t, isActive: checked } : t
                                    );
                                } else {
                                    updated = [...values.relatedPartyTypes, { type, isActive: checked }];
                                }
                                setFieldValue('relatedPartyTypes', updated);
                                // When AS is checked, default canEsign to true
                                if (type === 'authorisedsignatory' && checked) {
                                    setFieldValue('canEsign', true);
                                }
                                // When AS is unchecked, clear AS-specific fields
                                if (type === 'authorisedsignatory' && !checked) {
                                    setFieldValue('designation', '');
                                    setFieldValue('canEsign', false);
                                }
                                // When Director is unchecked, clear Director-specific fields
                                if (type === 'director' && !checked) {
                                    setFieldValue('din', '');
                                }
                                // When UBO is unchecked, clear UBO-specific fields
                                if (type === 'ubo' && !checked) {
                                    setFieldValue('countryOfTaxResidency', '');
                                    setFieldValue('percentageOfBeneficialInterest', 0);
                                    setFieldValue('cpUboCode', '');
                                    setFieldValue('placeAndCountryOfBirth', '');
                                    setFieldValue('ckycNumber', '');
                                    setFieldValue('gender', '');
                                    setFieldValue('occupation', '');
                                    setFieldValue('nationality', '');
                                }
                                // When Others is unchecked, clear the specify field
                                if (type === 'others' && !checked) {
                                    setFieldValue('otherRelatedPartyType', '');
                                }
                            };

                            const handleFetchData = async () => {
                                if (!values.pan || values.pan.length !== 10) return;
                                if (!values.dob) return;
                                try {
                                    setFetchLoading(true);
                                    const response = (await dispatch(FetchData({ panNumber: values.pan, dob: dayjs(values.dob).format('YYYY-MM-DD') }))) as any;
                                    if (response.amlCheck) {
                                        setAmlCheckPassed(true);
                                        setFieldValue('amlCheck', true);
                                    } else {
                                        setAmlCheckPassed(false);
                                        setFieldValue('amlCheck', false);
                                        setAmlFailedResponse(response.amlData?.system_match?.[0]);
                                        setAmlPopupOpen(true);
                                    }
                                    // Populate fields from KRA response if available
                                    if (response.data && Object.keys(response.data).length) {
                                        const d = response.data;
                                        setValues({
                                            ...values,
                                            dob: values.dob,
                                            name: d.name || values.name,
                                            fatherOrSpouseName: d.fatherName || d.fatherOrSpouseName || values.fatherOrSpouseName,
                                            gender: d.gender || values.gender,
                                            nationality: d.nationality ? d.nationality.toUpperCase() : values.nationality,
                                            email: d.email || values.email,
                                            mobile: d.mobile || values.mobile,
                                            correspondenceAddress1: d.address1 || values.correspondenceAddress1,
                                            correspondenceAddress2: d.address2 || values.correspondenceAddress2,
                                            correspondenceAddress3: d.address3 || values.correspondenceAddress3,
                                            correspondenceCity: d.city || values.correspondenceCity,
                                            correspondenceState: d.state || values.correspondenceState,
                                            correspondenceCountry: d.country ? d.country.toUpperCase() : values.correspondenceCountry,
                                            correspondencePincode: d.pincode || values.correspondencePincode,
                                            amlCheck: response.amlCheck,
                                        });
                                    }
                                    setPanVerified(true);
                                } catch (e) {
                                    console.error((e as Error).message);
                                } finally {
                                    setFetchLoading(false);
                                }
                            };

                            // Keep ref in sync with latest form values
                            React.useEffect(() => { formRefs.current[partyIndex] = values; }, [values]);
                            return (
                                <Box component="form" id={`related-party-form-${partyIndex}`} noValidate onSubmit={formikSubmit}>
                                    <Grid container rowSpacing={1}>
                                        {/* Type checkboxes */}
                                        <Grid item xs={12} sx={{ mb: 2 }}>
                                            <Typography variant="body_500" color="text.primary" sx={{ mb: 1.5 }}>Type of Related Party</Typography>
                                            <Box display="flex" gap={2} flexWrap="wrap">
                                                <FormControlLabel control={<Checkbox checked={hasType(values, 'director')} onChange={(e) => toggleType('director', e.target.checked)} size="small" />} label="Director" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={hasType(values, 'promoter')} onChange={(e) => toggleType('promoter', e.target.checked)} size="small" />} label="Promoter" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={hasType(values, 'karta')} onChange={(e) => toggleType('karta', e.target.checked)} size="small" />} label="Karta" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={hasType(values, 'trustee')} onChange={(e) => toggleType('trustee', e.target.checked)} size="small" />} label="Trustee" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={hasType(values, 'partner')} onChange={(e) => toggleType('partner', e.target.checked)} size="small" />} label="Partner" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={hasType(values, 'courtAppointed')} onChange={(e) => toggleType('courtAppointed', e.target.checked)} size="small" />} label="Court Appointed Official Proprietor" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={hasType(values, 'beneficiary')} onChange={(e) => toggleType('beneficiary', e.target.checked)} size="small" />} label="Beneficiary" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={isAS} onChange={(e) => toggleType('authorisedsignatory', e.target.checked)} size="small" />} label="Authorized Signatory" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={isUBO} onChange={(e) => toggleType('ubo', e.target.checked)} size="small" />} label="Beneficial Owner (UBO)" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={hasType(values, 'powerOfAttorneyHolder')} onChange={(e) => toggleType('powerOfAttorneyHolder', e.target.checked)} size="small" />} label="Power of Attorney Holder" sx={{ m: 0 }} />
                                                <FormControlLabel control={<Checkbox checked={hasType(values, 'others')} onChange={(e) => toggleType('others', e.target.checked)} size="small" />} label="Others" sx={{ m: 0 }} />
                                            </Box>
                                            {hasType(values, 'others') && (
                                                <Box sx={{ mt: 1, maxWidth: 300 }}>
                                                    <MFTextField name="otherRelatedPartyType" label="Specify Other *" placeholder="Specify other type" />
                                                </Box>
                                            )}
                                        </Grid>
                                        <Grid item xs={12}><SubHeading>Identity Details of Related Person</SubHeading></Grid>

                                        {/* PAN + DOB (always visible) */}
                                        <Grid item xs={12} sm={6}><MFTextField name="pan" label="PAN *" placeholder="Enter PAN" inputProps={{ style: { textTransform: 'uppercase' } }} disabled={isExistingParty} onChange={(e) => { if (!isExistingParty) { const newPan = e.target.value.toUpperCase(); if (panVerified) { setValues({ ...values, pan: newPan, name: '', fatherOrSpouseName: '', gender: '', nationality: '', email: '', mobile: '', designation: '', din: '', identificationType: '', identificationNumber: '', countryOfTaxResidency: '', percentageOfBeneficialInterest: 0, cpUboCode: '', placeAndCountryOfBirth: '', ckycNumber: '', occupation: '', correspondenceAddress1: '', correspondenceAddress2: '', correspondenceAddress3: '', correspondenceCity: '', correspondenceState: '', correspondenceCountry: '', correspondencePincode: '', correspondenceAddressType: '', permanentAddress1: '', permanentAddress2: '', permanentAddress3: '', permanentCity: '', permanentState: '', permanentCountry: '', permanentPincode: '', permanentAddressType: '', permanentAddressSameAsCorrespondence: 'N', amlCheck: false, amlComments: '', amlAcknowledgement: false }); setPanVerified(false); setAmlCheckPassed(false); } else { setFieldValue('pan', newPan); } } }} /></Grid>
                                        <Grid item xs={12} sm={6}><TextDatePicker label="Date of Birth *" placeholder="DD/MM/YYYY" name="dob" disabled={isExistingParty} onClick={() => { if (!isExistingParty && panVerified) { setPanVerified(false); setAmlCheckPassed(false); } }} /></Grid>

                                        {/* Fetch Data button - only for new parties */}
                                        {!isExistingParty && (
                                            <Grid item xs={12} display="flex" alignItems="center" sx={{ my: 1 }}>
                                                <LoadingButton
                                                    loadingPosition="start"
                                                    loading={fetchLoading}
                                                    variant="contained"
                                                    onClick={handleFetchData}
                                                    disabled={panVerified || !values.pan || values.pan.length !== 10 || !values.dob}
                                                    sx={{ fontSize: 13, minWidth: 120, fontWeight: 500, py: '6px', px: '16px' }}
                                                >
                                                    {panVerified ? 'Verified' : 'Fetch Data'}
                                                </LoadingButton>
                                                {panVerified && (
                                                    <Typography sx={{ ml: 1.5, fontSize: 13, fontWeight: 500, color: amlCheckPassed ? 'success.main' : 'error.main' }}>
                                                        {amlCheckPassed ? 'AML check passed' : 'AML check failed'}
                                                    </Typography>
                                                )}
                                            </Grid>
                                        )}

                                        {/* Remaining fields (only after PAN verified or on edit) */}
                                        {showAllFields && (<>
                                            <Grid item xs={12} sm={6}><MFTextField name="name" label="Name *" placeholder="Enter Name" /></Grid>
                                            <Grid item xs={12} sm={6}><MFTextField name="fatherOrSpouseName" label="Father/Spouse Name *" placeholder="Enter Name" /></Grid>
                                            {hasType(values, 'director') && (<>
                                                <Grid item xs={12} sm={6}><MFSelectField name="identificationType" label="Identity Type *" items={identityTypeOptions} /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="identificationNumber" label="ID Number *" placeholder="Enter ID Number" /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="din" label="DIN *" placeholder="Enter DIN" /></Grid>
                                            </>)}
                                            <Grid item xs={12} sm={6}><MFTextField name="email" label="Email *" placeholder="Enter Email" /></Grid>
                                            <Grid item xs={12} sm={6}><MFTextField name="mobile" label="Mobile *" placeholder="Enter Mobile" /></Grid>
                                            {isAS && (<Grid item xs={12} sm={6}><MFTextField name="designation" label="Designation *" placeholder="Enter Designation" /></Grid>)}
                                            {isUBO && (<>
                                                <Grid item xs={12} sm={6}><MFSelectField name="gender" label="Gender *" items={genderMasters.map((g: string) => ({ key: g, value: g }))} /></Grid>
                                                <Grid item xs={12} sm={6}><MFSelectField name="occupation" label="Occupation *" items={uboOccupationDetailsMasters.map((o: string) => ({ key: o, value: o }))} isCapitalize /></Grid>
                                                <Grid item xs={12} sm={6}><SearchableSelect name="nationality" label="Nationality *" items={countriesList.map((c) => ({ key: c.nationality || c.name, value: c.nationality || c.name }))} searchFieldPlaceholder="Search Nationality" /></Grid>
                                                <Grid item xs={12} sm={6}><SearchableSelect name="countryOfTaxResidency" label="Country of Tax Residency *" items={countriesList.map((c) => ({ key: c.name, value: c.name }))} searchFieldPlaceholder="Search Country" /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="percentageOfBeneficialInterest" label="UBO % *" placeholder="Enter %" type="number" onInput={(e: React.ChangeEvent<HTMLInputElement>) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ''); }} onWheel={(e: React.WheelEvent<HTMLInputElement>) => (e.target as HTMLInputElement).blur()} /></Grid>
                                                <Grid item xs={12} sm={6}><MFSelectField name="cpUboCode" label="UBO Code *" items={Object.keys(CpUboCodesMaster).map((k) => ({ key: CpUboCodesMaster[k], value: k }))} indexing={true} /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="placeAndCountryOfBirth" label="Place of Birth *" placeholder="Enter Place of Birth" /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="ckycNumber" label="CKYC Number" placeholder="Enter CKYC Number" /></Grid>
                                            </>)}

                                            {/* Correspondence Address */}
                                            <Grid item xs={12}><SubHeading>Correspondence Address</SubHeading></Grid>
                                            <Grid item xs={12} sm={6}><MFTextField name="correspondenceAddress1" label="Address Line 1 *" placeholder="Enter Address" /></Grid>
                                            <Grid item xs={12} sm={6}><MFTextField name="correspondenceAddress2" label="Address Line 2 *" placeholder="Enter Address" /></Grid>
                                            <Grid item xs={12} sm={6}><MFTextField name="correspondenceAddress3" label="Address Line 3 *" placeholder="Enter Address" /></Grid>
                                            <Grid item xs={12} sm={6}><MFTextField name="correspondenceCity" label="City *" placeholder="Enter City" /></Grid>
                                            <Grid item xs={12} sm={6}><MFTextField name="correspondenceState" label="State *" placeholder="Enter State" /></Grid>
                                            <Grid item xs={12} sm={6}><SearchableSelect name="correspondenceCountry" label="Country *" items={countriesList.map((c) => ({ key: c.name, value: c.name }))} searchFieldPlaceholder="Search Country" /></Grid>
                                            <Grid item xs={12} sm={6}><MFTextField name="correspondencePincode" label="Pincode *" placeholder="Enter Pincode" /></Grid>
                                            <Grid item xs={12} sm={6}><MFSelectField name="correspondenceAddressType" label="Address Type *" items={addressTypeOptions} /></Grid>

                                            {/* Permanent Address */}
                                            <Grid item xs={12}><SubHeading>Permanent Address</SubHeading></Grid>
                                            <Grid item xs={12}>
                                                <FormControlLabel control={<Checkbox checked={values.permanentAddressSameAsCorrespondence === 'Y'} onChange={(e) => { setFieldValue('permanentAddressSameAsCorrespondence', e.target.checked ? 'Y' : 'N'); }} />} label="Same as Correspondence Address" />
                                            </Grid>
                                            {values.permanentAddressSameAsCorrespondence !== 'Y' && (<>
                                                <Grid item xs={12} sm={6}><MFTextField name="permanentAddress1" label="Address Line 1 *" placeholder="Enter Address" /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="permanentAddress2" label="Address Line 2 *" placeholder="Enter Address" /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="permanentAddress3" label="Address Line 3 *" placeholder="Enter Address" /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="permanentCity" label="City *" placeholder="Enter City" /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="permanentState" label="State *" placeholder="Enter State" /></Grid>
                                                <Grid item xs={12} sm={6}><SearchableSelect name="permanentCountry" label="Country *" items={countriesList.map((c) => ({ key: c.name, value: c.name }))} searchFieldPlaceholder="Search Country" /></Grid>
                                                <Grid item xs={12} sm={6}><MFTextField name="permanentPincode" label="Pincode *" placeholder="Enter Pincode" /></Grid>
                                                <Grid item xs={12} sm={6}><MFSelectField name="permanentAddressType" label="Address Type *" items={addressTypeOptions} /></Grid>
                                            </>)}
                                        </>)}
                                        {/* Hidden submit for programmatic validation trigger */}
                                        <button type="submit" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
                                    </Grid>
                                    {/* AML Popup */}
                                    <AmlPopup
                                        handleClose={() => setAmlPopupOpen(false)}
                                        amlPopupOpen={amlPopupOpen}
                                        onSubmit={(amlValues: AmlPopupValues) => {
                                            setFieldValue('amlComments', amlValues.amlComments);
                                            setFieldValue('amlAcknowledgement', amlValues.amlAcknowledgement);
                                            setAmlPopupOpen(false);
                                        }}
                                        FURTHER_INFORMATION={amlFailedResponse?.FURTHER_INFORMATION}
                                        EXTERNAL_SOURCES={amlFailedResponse?.EXTERNAL_SOURCES}
                                    />
                                </Box>
                            );
                        }}
                    </Formik>
                    {partyIndex < parties.length - 1 && (
                        <Box sx={(t) => ({ borderBottom: `1px dashed ${t.palette.grey[300]}`, my: 3 })} />
                    )}
                </Grid>
            ))}

            {/* Cancel / Save */}
            <Grid item xs={12} lg={6} sx={{ mt: 4 }}>
                <Button variant="outlined" fullWidth onClick={onCancel}
                    sx={(theme) => ({ color: theme.palette.text.secondary, border: `0.5px solid ${theme.palette.text.secondary}`, borderRadius: '10px', padding: '10px', '&:hover, &.Mui-focusVisible': { borderColor: theme.palette.text.secondary, backgroundColor: theme.palette.background.default } })}>
                    Cancel
                </Button>
            </Grid>
            <Grid item xs={12} lg={6} sx={{ mt: { xs: 2, lg: 4 } }}>
                <Button variant="contained" fullWidth onClick={handleSaveAll}
                    sx={(theme) => ({ padding: '10px', borderRadius: '10px', backgroundColor: theme.palette.primary[500], color: theme.palette.textDark.primary })}>
                    Save
                </Button>
            </Grid>
        </Grid>
    );
}

export default function RelatedParties(): JSX.Element {
    const [relatedPartiesList, setRelatedPartiesList] = useState<RelatedParty[]>([]);
    const [requiredAS, setRequiredAS] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<'list' | 'form'>('list');
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [saveType, setSaveType] = useState('');
    const [deletePopup, setDeletePopup] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuIndex, setMenuIndex] = useState<number | null>(null);
    const [countriesList, setCountriesList] = useState<mdmsCountriesList[]>([]);
    const [thresholdInfoOpen, setThresholdInfoOpen] = useState(false);

    const { application } = useSelector((store: RootStateType) => store.application);
    const { role = '' } = useSelector((store: RootStateType) => store.auth);
    const dispatch = useDispatch();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();

    useEffect(() => {
        (async function () {
            try {
                const res = (await dispatch(getNationalityList())) as unknown as nationaliyType;
                setCountriesList(res.countries);
            } catch (e) { console.error((e as Error).message); }
        })();
    }, []);

    useEffect(() => {
        const relatedParties = application?.relatedParties ?? [];
        setRelatedPartiesList(relatedParties.map((p) => ({ ...createInitialParty(), ...p })));
        if (application?.groupSignatoriesThreshold) {
            setRequiredAS(application.groupSignatoriesThreshold);
        }
        setCurrentPage('list');
    }, [application]);

    const activeParties = relatedPartiesList.filter((p) =>
        p.relatedPartyTypes?.some((t) => t.isActive)
    );
    const asCount = activeParties.filter((p) => hasType(p, 'authorisedsignatory')).length;
    const uboParties = activeParties.filter((p) => hasType(p, 'ubo'));

    // Menu handlers
    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, idx: number) => {
        setAnchorEl(e.currentTarget); setMenuIndex(idx);
    };
    const handleMenuClose = () => { setAnchorEl(null); setMenuIndex(null); };

    const handleEdit = (idx: number) => { setEditIndex(idx); setCurrentPage('form'); handleMenuClose(); };
    const handleDeleteClick = (idx: number) => { setDeleteIndex(idx); setDeletePopup(true); handleMenuClose(); };

    const handleDeleteConfirm = () => {
        if (deleteIndex !== null) {
            const target = activeParties[deleteIndex];
            setRelatedPartiesList(relatedPartiesList.map((p) => {
                if (p === target) {
                    return {
                        ...p,
                        relatedPartyTypes: p.relatedPartyTypes.map((t) => ({ ...t, isActive: false })),
                    };
                }
                return p;
            }));
        }
        setDeletePopup(false); setDeleteIndex(null);
    };

    const handleFormSave = (values: RelatedParty) => {
        if (editIndex !== null) {
            const target = activeParties[editIndex];
            setRelatedPartiesList(relatedPartiesList.map((p) => p === target ? { ...values } : p));
        } else {
            setRelatedPartiesList([...relatedPartiesList, { ...values }]);
        }
        setCurrentPage('list'); setEditIndex(null);
    };

    const handleFormCancel = () => { setCurrentPage('list'); setEditIndex(null); };

    // Validations on Continue
    const validateBeforeSubmit = (): string | null => {
        // Threshold must be selected
        if (!requiredAS) return 'Please select the Total No. of required authorised signatories';
        // 0. Every party must have at least one type
        for (const party of activeParties) {
            const hasAnyType = party.relatedPartyTypes?.some((t) => t.isActive);
            if (!hasAnyType) return `Please select at least one checkbox under 'Type of Related Party'`;
        }
        // 1. At least one AS with eSign checked
        const canEsignCount = activeParties.filter((p) => hasType(p, 'authorisedsignatory') && p.canEsign).length;
        if (canEsignCount === 0) return 'At least one authorised signatory must be selected for eSign.';
        // 2. canEsign count >= required threshold
        if (canEsignCount < requiredAS) return 'The No. of authorized signatories selected for eSign must not be less than the required No. of authorized signatories.';
        // 3. UBO declaration "none"/others requires at least one UBO
        const { ubo_declaration_type = '' } = application || {};
        if (ubo_declaration_type === 'none' && uboParties.length === 0) {
            return 'Please add at least one UBO as per UBO Declaration';
        }
        // 4. UBO % validation
        const totalUboPercent = uboParties.reduce((sum, p) => sum + Number(p.percentageOfBeneficialInterest || 0), 0);
        if (uboParties.length > 0 && totalUboPercent > 100) {
            return 'Total UBO percentage cannot exceed 100%';
        }
        for (const uboP of uboParties) {
            if (Number(uboP.percentageOfBeneficialInterest) <= 0) {
                return `UBO percentage for ${uboP.name || 'a party'} must be greater than 0`;
            }
        }
        return null;
    };

    const handleSubmit = async (saveTypeText: string) => {
        try {
            if (saveTypeText === 'save and proceed') {
                const validationError = validateBeforeSubmit();
                if (validationError) { dispatch(showError(validationError)); return; }
            }
            const { id, applicant1ReferenceId = '', currentStep, applicationNumber } = application || {};
            const payload = relatedPartiesList.map((p) => ({
                ...p, name: removeSingleQuote(p.name), email: removeSingleQuote(p.email),
                dob: p.dob ? dayjs(p.dob).format('YYYY-MM-DD') : '',
            }));

            const checkApplication = applicationComparison(
                { ...application },
                {
                    ...application, relatedParties: payload, requiredAuthorisedSignatories: requiredAS, groupSignatoriesThreshold: requiredAS,
                    currentStep: !!currentStep && currentStep > (ONBOARDING_DEMAT_DETAILS ? 5 : 4) ? currentStep : (ONBOARDING_DEMAT_DETAILS ? 6 : 5)
                }
            );

            let currentStatus = application?.status;
            if (!isModuleAllowed({ value: role ?? '', compareArr: [USER_ROLES.AMCAPPROVER] }) && application?.status === 'sent_to_amc_approver') {
                currentStatus = 'draft';
            }

            if (id && !checkApplication) {
                setSaveType(saveTypeText); setLoading(true);
                const response = await dispatch(updateApplication({
                    body: {
                        ...application, relatedParties: payload, requiredAuthorisedSignatories: requiredAS, groupSignatoriesThreshold: requiredAS,
                        currentStep: ONBOARDING_DEMAT_DETAILS ? 6 : 5, status: currentStatus
                    },
                    applicationId: id,
                    ...(saveTypeText !== 'save and proceed' && { toastMessage: '' }),
                }));
                if (!response) return;
                saveTypeText === 'save and proceed'
                    ? history.push('fatca', { id, applicant1ReferenceId })
                    : history.push(saveForLater(role, id, applicant1ReferenceId));
            } else if (checkApplication) {
                if (saveTypeText !== 'save and proceed') {
                    enqueueSnackbar(`Application ${applicationNumber} - Saved successfully`, { variant: 'success', autoHideDuration: 3000 });
                }
                saveTypeText === 'save and proceed'
                    ? history.push('fatca', { id, applicant1ReferenceId })
                    : history.push(saveForLater(role, id, applicant1ReferenceId));
            }
        } catch (e) {
            typeof e === 'string' && dispatch(showError(e));
            console.error((e as Error).message);
        } finally { setLoading(false); }
    };

    const asCheckboxCell = {
        header: (): JSX.Element => {
            const [open, setOpen] = useState(false);
            return (
                <>
                    <Typography
                        variant="h6"
                        sx={{ fontFamily: `${theme.typography.fontFamily}`, fontSize: 14, fontWeight: 500, color: 'primary.main', pr: 0.5 }}
                        component="span">
                        Send e-sign link
                    </Typography>
                    <InfoOutlinedIcon
                        color="info"
                        fontSize="small"
                        onClick={() => setOpen(true)}
                        sx={{ position: 'relative', top: 4, cursor: 'pointer' }}
                    />
                    <Dialog
                        onClose={() => setOpen(false)}
                        open={open}
                        sx={{ '.MuiPaper-root': { maxWidth: 400, p: { xs: 2, sm: '15px 20px 45px' }, borderRadius: '10px', height: 'unset', overflowY: 'auto' } }}>
                        <IconButton onClick={() => setOpen(false)} sx={{ alignSelf: 'flex-end' }}>
                            <CloseIcon fontSize="medium" />
                        </IconButton>
                        <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
                            <InfoOutlinedIcon color="info" fontSize="large" />
                        </Box>
                        <Box sx={{ width: { xs: '100%', sm: '80%' }, mx: 'auto', '.MuiTypography-root': { fontSize: 20, fontWeight: 500, textAlign: 'center' } }}>
                            <Typography sx={{ color: 'secondary.main', lineHeight: '35px' }}>
                                Use this option to send the e-sign link to specific set of authorized signatories.
                            </Typography>
                        </Box>
                    </Dialog>
                </>
            );
        },
        renderEditDeleteCell: (row: RelatedParty, index: number) => {
            const isAS = hasType(row, 'authorisedsignatory');
            return (
                <Checkbox
                    checked={row.canEsign}
                    disabled={!isAS}
                    sx={{ p: 0 }}
                    onChange={() => {
                        const updatedList = relatedPartiesList.map((p) => {
                            if (p === activeParties[index]) {
                                return { ...p, canEsign: !p.canEsign };
                            }
                            return p;
                        });
                        setRelatedPartiesList(updatedList);
                    }}
                    inputProps={{ 'aria-label': `Can E-Sign for ${row.name || 'party'}` } as React.InputHTMLAttributes<HTMLInputElement>}
                />
            );
        },
    };

    const actionsCell = {
        header: 'ACTIONS',
        renderEditDeleteCell: (row: RelatedParty, index: number) => (
            <IconButton
                onClick={(e) => handleMenuOpen(e, index)}
                size="small"
                aria-label={`Actions for ${row.name || 'party'}`}
                aria-haspopup="true"
            >
                <MoreVertIcon />
            </IconButton>
        ),
    };

    // ==================== FORM VIEW (Page 2) ====================
    if (currentPage === 'form') {
        const formInit: RelatedParty[] = editIndex !== null
            ? [{ ...createInitialParty(), ...activeParties[editIndex] }]
            : [createInitialParty()];

        return (
            <FormPageView
                initialParties={formInit}
                isEdit={editIndex !== null}
                onSave={(parties: RelatedParty[]) => {
                    if (editIndex !== null) {
                        // Edit: replace the one party
                        const target = activeParties[editIndex];
                        const updatedList = relatedPartiesList.map((p) => p === target ? { ...parties[0] } : p);
                        setRelatedPartiesList(updatedList);
                    } else {
                        // Add: append all new parties
                        setRelatedPartiesList([...relatedPartiesList, ...parties]);
                    }
                    setCurrentPage('list'); setEditIndex(null);
                }}
                onCancel={handleFormCancel}
                countriesList={countriesList}
                currentActiveCount={activeParties.length}
                existingParties={editIndex !== null ? activeParties.filter((_, i) => i !== editIndex) : activeParties}
            />
        );
    }

    // ==================== LIST VIEW (Page 1) ====================
    return (
        <Grid container rowSpacing={1} sx={{ width: '100%', ml: 0, '.MuiGrid-item': { px: { xs: 0, sm: '15px' } } }}>
            {/* Required AS threshold */}
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 1 }}>
                <Typography sx={{ color: 'primary.main', fontSize: '16px' }}>
                    Total No. of required authorised signatories
                    <Typography component="span" sx={{ color: 'error.main' }}>*</Typography> :
                </Typography>
                <InfoOutlinedIcon
                    color="info"
                    fontSize="small"
                    onClick={() => setThresholdInfoOpen(true)}
                    sx={{ ml: 0.5, cursor: 'pointer', position: 'relative', top: 2 }}
                />
                <Dialog
                    onClose={() => setThresholdInfoOpen(false)}
                    open={thresholdInfoOpen}
                    sx={{ '.MuiPaper-root': { maxWidth: 400, p: { xs: 2, sm: '15px 20px 45px' }, borderRadius: '10px', height: 'unset', overflowY: 'auto' } }}>
                    <IconButton onClick={() => setThresholdInfoOpen(false)} sx={{ alignSelf: 'flex-end' }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                    <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
                        <InfoOutlinedIcon color="info" fontSize="large" />
                    </Box>
                    <Box sx={{ width: { xs: '100%', sm: '80%' }, mx: 'auto', '.MuiTypography-root': { fontSize: 20, fontWeight: 500, textAlign: 'center' } }}>
                        <Typography sx={{ color: 'secondary.main', lineHeight: '35px' }}>
                            Please enter no of Signatories Authorised to e-Sign the Digital On-Boarding Kit as in Board Resolution (Severally/Jointly)
                        </Typography>
                    </Box>
                </Dialog>
                <Box sx={(t) => ({ width: '100px', ml: 2, '.MuiOutlinedInput-root': { marginTop: '0', backgroundColor: `${t.palette.primary.main}12`, '&.MuiInputBase-root': { '& .MuiSelect-select': { padding: '7px 38px', border: 'none' } }, '.MuiOutlinedInput-notchedOutline': { border: 'none' } }, svg: { color: t.palette.primary.main } })}>
                    <Select
                        labelId="required-as-label"
                        id="required-as-select"
                        value={requiredAS || ''}
                        displayEmpty
                        onChange={(e) => setRequiredAS(Number(e.target.value))}
                        sx={{ color: 'primary.main', fontWeight: 500 }}
                        inputProps={{ 'aria-label': 'Total number of required authorised signatories' }}
                    >
                        {MAX_REQUIRED_AUTHORISED_SIGNATORIES.map((val, i) => (
                            <MenuItem key={i} value={val} sx={(t) => ({ borderBottom: `1px solid ${t.palette.grey[300]}`, '&:last-child': { border: 'none' }, justifyContent: 'center' })}>{val}</MenuItem>
                        ))}
                    </Select>
                </Box>
            </Grid>

            <Grid item xs={12}><SubHeading>Related Parties List</SubHeading></Grid>

            {/* Add New Party */}
            {activeParties.length < MAX_RELATED_PARTIES && (
                <Grid item xs={12} display="flex" sx={{ justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" startIcon={<AddIcon />} sx={{ color: 'primary.main', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}
                        onClick={() => { setEditIndex(null); setCurrentPage('form'); }}>
                        Add New Party
                    </Button>
                </Grid>
            )}

            {/* Table */}
            {activeParties.length > 0 && (
                <Grid item xs={12} sm={12} lg={12}>
                    <DataTable
                        tableData={activeParties}
                        tableHeader={[asCheckboxCell, ...headers, actionsCell]}
                        renderAdditionalRow={false}
                        tableHeaderCustomStyles={{ '.MuiTableCell-head': { fontFamily: `${theme.typography.fontFamily}`, fontSize: 14, fontWeight: 500, color: 'primary.main' } }}
                        rowCustomStyles={{ '.MuiTableCell-root': { py: '8px', overflowWrap: 'anywhere' } }}
                        boxStyles={{ minHeight: '0px', minWidth: 450 }}
                    />
                </Grid>
            )}

            {/* Actions Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} MenuListProps={{ 'aria-label': 'Related party actions' }}>
                <MenuItem onClick={() => { if (menuIndex !== null) handleEdit(menuIndex); }}>Edit</MenuItem>
                <MenuItem onClick={() => { if (menuIndex !== null) handleDeleteClick(menuIndex); }}>Delete</MenuItem>
            </Menu>

            {/* Delete Dialog */}
            <ConfirmationDialog open={deletePopup} setOpen={() => setDeletePopup(false)} onSave={handleDeleteConfirm} onCancel={() => setDeletePopup(false)} message="Deleting New Party details" subMessage="Are you sure you want to delete the new party details?" showIcon={true} />

            {/* Save / Continue */}
            <Grid item xs={12} sx={{ mt: 4 }} />
            <ProceedSaveLater saveLater={() => handleSubmit('save for later')} saveAndProceed={() => handleSubmit('save and proceed')} loader={loading} clickedButton={saveType} />
        </Grid>
    );
}
